// owner: orgs-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import {
  EmailSchema,
  EpochMsSchema,
  HandleSchema,
  OrgRoleSchema,
  SlugSchema,
  UlidSchema,
  UrlSchema,
} from './common';

export const OrgCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  /** Defaults to a slug of the name. */
  slug: SlugSchema.min(2).optional(),
  about: z.string().trim().max(5000).default(''),
  website: UrlSchema.nullish(),
  industry: z.string().trim().max(100).default(''),
  size: z.string().trim().max(40).default(''),
  location: z.string().trim().max(200).default(''),
  seoEnabled: z.boolean().default(true),
  geoEnabled: z.boolean().default(true),
});
export type OrgCreate = z.infer<typeof OrgCreateSchema>;

export const OrgUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    about: z.string().trim().max(5000),
    website: UrlSchema.nullable(),
    industry: z.string().trim().max(100),
    size: z.string().trim().max(40),
    location: z.string().trim().max(200),
    seoEnabled: z.boolean(),
    geoEnabled: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update' });

export const OrgIdParamSchema = z.object({ id: UlidSchema });
export const OrgSlugParamSchema = z.object({ slug: SlugSchema });
export const OrgMemberParamSchema = z.object({ id: UlidSchema, userId: UlidSchema });
export const OrgInvitationIdParamSchema = z.object({ inviteId: UlidSchema });
export const OrgInvitationParamSchema = z.object({ id: UlidSchema, inviteId: UlidSchema });

/**
 * Exactly one of handle / email / userId identifies the person to invite. This never resolves to
 * membership directly (B7): it only ever creates a pending invitation, so the response must stay
 * identical for a known, unknown or already-member target — never echo a resolved profile field.
 * `approvalId` lets an agent retry an owner/admin role grant with a human-granted approval.
 */
export const OrgMemberAddSchema = z
  .object({
    handle: HandleSchema.optional(),
    email: EmailSchema.optional(),
    userId: UlidSchema.optional(),
    role: OrgRoleSchema,
    approvalId: UlidSchema.optional(),
  })
  .refine((v) => [v.handle, v.email, v.userId].filter((x) => x !== undefined).length === 1, {
    message: 'Provide exactly one of handle, email or userId',
  });
export const OrgMemberRolesSchema = z.object({
  roles: z
    .array(OrgRoleSchema)
    .min(1)
    .max(6)
    .transform((r) => [...new Set(r)]),
  /** Required when the new role set includes owner/admin and the caller is an agent (B7). */
  approvalId: UlidSchema.optional(),
});

export const ORG_INVITATION_STATUSES = [
  'pending',
  'accepted',
  'declined',
  'revoked',
  'expired',
] as const;
export const OrgInvitationStatusSchema = z.enum(ORG_INVITATION_STATUSES);
export const OrgInvitationIdentifierTypeSchema = z.enum(['handle', 'email', 'userId']);

/** Ack for POST .../members|invitations: identical for known, unknown and already-member targets. */
export const OrgInviteAckSchema = z.object({ status: z.literal('invited') });

/** Admin-facing invitation row: only what the admin typed — never a resolved profile field. */
export const OrgInvitationSchema = z.object({
  id: UlidSchema,
  orgId: UlidSchema,
  identifierType: OrgInvitationIdentifierTypeSchema,
  identifier: z.string(),
  role: OrgRoleSchema,
  invitedBy: UlidSchema,
  status: OrgInvitationStatusSchema,
  createdAt: EpochMsSchema,
  expiresAt: EpochMsSchema,
});
export const OrgInvitationsSchema = z.object({ items: z.array(OrgInvitationSchema) });

/** Invitee-facing view: enough to decide, no admin/inviter identity. */
export const OrgMyInvitationSchema = z.object({
  id: UlidSchema,
  orgId: UlidSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  role: OrgRoleSchema,
  status: OrgInvitationStatusSchema,
  createdAt: EpochMsSchema,
  expiresAt: EpochMsSchema,
});
export const OrgMyInvitationsSchema = z.object({ items: z.array(OrgMyInvitationSchema) });

export const OrgInvitationDecisionSchema = z.object({
  id: UlidSchema,
  status: OrgInvitationStatusSchema,
});

export const OrgSchema = z.object({
  id: UlidSchema,
  slug: z.string(),
  name: z.string(),
  about: z.string(),
  website: z.string().nullable(),
  logoUrl: z.string().nullable(),
  industry: z.string(),
  size: z.string(),
  location: z.string(),
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type Org = z.infer<typeof OrgSchema>;

/** Public page data: org + open-job count. Fields the owner has not opted out of only. */
export const OrgPublicSchema = OrgSchema.extend({ openJobCount: z.number().int().nonnegative() });

export const OrgMemberSchema = z.object({
  userId: UlidSchema,
  displayName: z.string(),
  handle: z.string().nullable(),
  roles: z.array(OrgRoleSchema),
});
export const OrgMembersSchema = z.object({ items: z.array(OrgMemberSchema) });
export const MyOrgsSchema = z.object({
  items: z.array(OrgSchema.extend({ roles: z.array(OrgRoleSchema) })),
});
export const OrgLogoSchema = z.object({ logoUrl: z.string() });

export const ORG_LOGO_MAX_BYTES = 1_000_000;
/** SVG is excluded on purpose: it can carry script. */
export const ORG_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/** Roles that may manage jobs and review applications. */
export const RECRUITING_ROLES = ['owner', 'admin', 'recruiter'] as const;
export const ORG_ADMIN_ROLES = ['owner', 'admin'] as const;
